import React, { useEffect, useMemo, useState } from "react";
import {
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  RefreshCcw,
  RefreshCw,
  Search,
  ShoppingBag,
  XCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  BuyFulfillmentCounts,
  BuyFulfillmentDeliveryTypeOption,
  BuyFulfillmentOrder,
  buyAdminApi,
} from "../../../services/buyAdminApi";
import {
  formatEventSourceDate,
  formatEventSourceTime,
  pickEventDateSource,
} from "../../../utils/eventDateTime";
import BuyAdminThemeToggle from "../shared/BuyAdminThemeToggle";
import "../shared/buyAdminThemeGlobals.css";
import styles from "./Fulfillment.module.css";

const PAGE_SIZE = 50;

const emptyCounts: BuyFulfillmentCounts = {
  pending: 0,
  confirmed: 0,
  fulfilled: 0,
  cancelled: 0,
  refunded: 0,
  all: 0,
};

const statusConfig = {
  pending: { label: "Pending", icon: Clock, color: styles.pending, bg: styles.pendingBg, pill: styles.pillPending },
  confirmed: { label: "Confirmed", icon: CheckCircle, color: styles.confirmed, bg: styles.confirmedBg, pill: styles.pillConfirmed },
  fulfilled: { label: "Fulfilled", icon: ShoppingBag, color: styles.fulfilled, bg: styles.fulfilledBg, pill: styles.pillFulfilled },
  cancelled: { label: "Cancelled", icon: XCircle, color: styles.cancelled, bg: styles.cancelledBg, pill: styles.pillCancelled },
  refunded: { label: "Refunded", icon: RefreshCcw, color: styles.refunded, bg: styles.refundedBg, pill: styles.pillRefunded },
};

const datePresets = [
  { key: "all", label: "All" },
  { key: "today", label: "Today" },
  { key: "next2", label: "Next 2 Days" },
  { key: "next7", label: "Next 7 Days" },
  { key: "next30", label: "Next 30 Days" },
  { key: "next90", label: "Next 90 Days" },
  { key: "custom", label: "Custom" },
] as const;

const defaultDeliveryTypeLabel = (value: string | null | undefined, options: BuyFulfillmentDeliveryTypeOption[]) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "-";
  const match = options.find((option) => option.value.toLowerCase() === normalized.toLowerCase());
  return match?.label || normalized;
};

type DatePreset = typeof datePresets[number]["key"];
type StatusKey = keyof typeof statusConfig;

const formatCurrency = (value: number | null | undefined) =>
  Number(value || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatEventDate = (order: BuyFulfillmentOrder, includeYear = true) =>
  formatEventSourceDate(pickEventDateSource(order), includeYear);

const formatEventTime = (order: BuyFulfillmentOrder) => {
  const source = pickEventDateSource(order);
  return order.event_time || formatEventSourceTime(source);
};

const formatInHandDate = (value: string | null | undefined, includeYear = false) =>
  formatEventSourceDate(value, includeYear);

const formatDate = (value: string | null | undefined, includeYear = true) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(includeYear ? { year: "numeric" as const } : {}),
  });
};

const formatTime = (value: string | null | undefined) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
};

const toLocalDate = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDateRange = (preset: DatePreset, from: string, to: string) => {
  if (preset === "custom") return { from: from || undefined, to: to || undefined };
  if (preset === "all") return {};

  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  if (preset === "next2") end.setDate(end.getDate() + 1);
  if (preset === "next7") end.setDate(end.getDate() + 6);
  if (preset === "next30") end.setDate(end.getDate() + 29);
  if (preset === "next90") end.setDate(end.getDate() + 89);

  return { from: toLocalDate(start), to: toLocalDate(end) };
};


const escapeCsv = (value: unknown) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const BuyAdminFulfillment = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<BuyFulfillmentOrder[]>([]);
  const [counts, setCounts] = useState<BuyFulfillmentCounts>(emptyCounts);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [datePreset, setDatePreset] = useState<DatePreset>("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [deliveryTypeOptions, setDeliveryTypeOptions] = useState<BuyFulfillmentDeliveryTypeOption[]>([]);
  const [deliveryTypeFilter, setDeliveryTypeFilter] = useState("");
  const [inHandFrom, setInHandFrom] = useState("");
  const [inHandTo, setInHandTo] = useState("");
  const [sortBy, setSortBy] = useState("purchasedAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const [refreshVersion, setRefreshVersion] = useState(0);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const loadOrders = async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const range = getDateRange(datePreset, customFrom, customTo);
      const response = await buyAdminApi.getFulfillmentOrders({
        status: statusFilter,
        search: search.trim() || undefined,
        delivery_type: deliveryTypeFilter || undefined,
        date_from: range.from,
        date_to: range.to,
        in_hand_from: inHandFrom || undefined,
        in_hand_to: inHandTo || undefined,
        sort_by: sortBy,
        sort_dir: sortDir,
        page,
        limit: PAGE_SIZE,
      });

      setOrders(response.orders || []);
      setCounts(response.counts || emptyCounts);
      setTotal(response.total || 0);
      setDeliveryTypeOptions(response.filters?.delivery_types || []);
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to load fulfillment orders.");
      setOrders([]);
      setTotal(0);
      setCounts(emptyCounts);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    statusFilter,
    search,
    datePreset,
    customFrom,
    customTo,
    deliveryTypeFilter,
    inHandFrom,
    inHandTo,
    sortBy,
    sortDir,
    page,
    refreshVersion,
  ]);

  useEffect(() => {
    const timer = window.setInterval(() => setRefreshVersion((value) => value + 1), 30000);
    return () => window.clearInterval(timer);
  }, []);

  const toggleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir((value) => (value === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortDir("desc");
    }
    setPage(1);
  };

  const exportCsv = () => {
    const headers = [
      "Order #",
      "Status",
      "Order Date",
      "Event",
      "Performer",
      "Event Date",
      "Event Time",
      "Venue",
      "Section",
      "Row",
      "Qty",
      "Price/Ticket",
      "Total Cost",
      "Total Spent",
      "Saved",
      "Customer",
      "Email",
      "Card Last 4",
      "In-Hand Date",
      "Delivery Type",
    ];
    const rows = orders.map((order) => [
      order.order_number,
      order.status,
      formatDate(order.purchased_at),
      order.event_name,
      order.performer,
      formatEventDate(order),
      formatEventTime(order),
      order.venue,
      order.section,
      order.row,
      order.quantity,
      order.price_per_ticket,
      order.total_ticket_cost,
      order.total_spent,
      order.total_saved,
      order.customer_name,
      order.customer_email,
      order.card_last4,
      formatInHandDate(order.in_hand_date),
      defaultDeliveryTypeLabel(order.fulfillment_type, deliveryTypeOptions),
    ]);
    const csv = [headers, ...rows].map((row) => row.map(escapeCsv).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "fulfillment_orders.csv";
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const sortMarker = (field: string) => (sortBy === field ? (sortDir === "asc" ? " ↑" : " ↓") : "");

  const kpiTiles = useMemo(() => Object.entries(statusConfig) as Array<[StatusKey, typeof statusConfig[StatusKey]]>, []);

  return (
    <div className={`${styles.page} buy-admin-theme-scope`} data-testid="buy-fulfillment-page">
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <p>Order lifecycle & ticket fulfillment operations</p>
          <h1>Fulfillment</h1>
        </div>
        <div className={styles.headerSearch}>
          <div className={styles.searchBox}>
            <Search size={17} />
            <input
              value={search}
              onChange={(event) => { setSearch(event.target.value); setPage(1); }}
              placeholder="Search user by name/email/phone..."
            />
          </div>
        </div>
        <div className={`${styles.headerActions} buy-admin-theme-actions`}>
          <button className={styles.outlineButton} type="button" onClick={exportCsv}>
            <Download size={16} />
            Export CSV
          </button>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={() => void loadOrders()}
            disabled={loading}
            aria-label="Refresh orders"
            title="Refresh"
          >
            <RefreshCw size={16} className={loading ? styles.spin : undefined} />
          </button>
          <BuyAdminThemeToggle iconOnly />
        </div>
      </div>

      <div className={styles.kpiGrid} data-testid="fulfillment-kpi-tiles">
        {kpiTiles.map(([key, config]) => {
          const Icon = config.icon;
          const active = statusFilter === key;
          return (
            <button
              key={key}
              className={`${styles.kpiCard} ${config.bg} ${active ? styles.kpiCardActive : ""}`}
              type="button"
              onClick={() => { setStatusFilter(active ? "all" : key); setPage(1); }}
              data-testid={`kpi-${key}`}
            >
              <Icon size={24} className={config.color} />
              <span className={config.color}>{config.label}</span>
              <strong className={config.color}>{counts[key]}</strong>
            </button>
          );
        })}
      </div>

      <div className={styles.filtersCard} data-testid="fulfillment-filters">
        <div className={styles.filterRow}>
          <div className={styles.searchBox}>
            <Search size={17} />
            <input
              value={search}
              onChange={(event) => { setSearch(event.target.value); setPage(1); }}
              placeholder="Search order #, customer, event..."
              data-testid="fulfillment-search"
            />
          </div>
          <div className={styles.datePresets}>
            {datePresets.map((preset) => (
              <button
                key={preset.key}
                type="button"
                className={`${styles.presetButton} ${datePreset === preset.key ? styles.presetButtonActive : ""}`}
                onClick={() => { setDatePreset(preset.key); setPage(1); }}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>
        <div className={styles.filterActions}>
          <select value={deliveryTypeFilter} onChange={(event) => { setDeliveryTypeFilter(event.target.value); setPage(1); }}>
            <option value="">Delivery Type: All</option>
            {deliveryTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {datePreset === "custom" ? (
            <div className={styles.datePair}>
              <span>Event Date</span>
              <input type="date" value={customFrom} onChange={(event) => { setCustomFrom(event.target.value); setPage(1); }} />
              <input type="date" value={customTo} onChange={(event) => { setCustomTo(event.target.value); setPage(1); }} />
            </div>
          ) : (
            <button className={styles.filterLink} type="button" onClick={() => setDatePreset("custom")}>
              <Clock size={14} />
              Filter
            </button>
          )}
          <button
            type="button"
            className={styles.outlineButton}
            onClick={() => {
              setSearch("");
              setStatusFilter("all");
              setDatePreset("all");
              setCustomFrom("");
              setCustomTo("");
              setDeliveryTypeFilter("");
              setInHandFrom("");
              setInHandTo("");
              setPage(1);
            }}
          >
            Clear
          </button>
          <div className={styles.totalLabel}>{total} orders</div>
        </div>
      </div>

      {errorMessage && <div className={styles.emptyState}>{errorMessage}</div>}

      <div className={styles.tableCard}>
        {loading ? (
          <div className={styles.emptyState}>Loading orders...</div>
        ) : orders.length === 0 ? (
          <div className={styles.emptyState}>No orders found matching filters</div>
        ) : (
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.orderColumn}>
                    <button className={styles.sortButton} type="button" onClick={() => toggleSort("orderNumber")}>
                      ORDER #{sortMarker("orderNumber")}
                    </button>
                  </th>
                  <th className={styles.customerColumn}>CUSTOMER</th>
                  <th className={styles.statusColumn}>STATUS</th>
                  <th className={styles.dateColumn}>
                    <button className={styles.sortButton} type="button" onClick={() => toggleSort("purchasedAt")}>
                      ORDER DATE{sortMarker("purchasedAt")}
                    </button>
                  </th>
                  <th className={styles.deliveryColumn}>DELIVERY TYPE</th>
                  <th>EVENT</th>
                  <th>
                    <button className={styles.sortButton} type="button" onClick={() => toggleSort("eventDate")}>
                      EVENT DATE{sortMarker("eventDate")}
                    </button>
                  </th>
                  <th>VENUE</th>
                  <th className={styles.center}>SEATING</th>
                  <th className={styles.center}>QTY</th>
                  <th className={styles.right}>$/TICKET</th>
                  <th className={styles.right}>
                    <button className={styles.sortButton} type="button" onClick={() => toggleSort("totalSpent")}>
                      TOTAL{sortMarker("totalSpent")}
                    </button>
                  </th>
                  <th className={styles.right}>SAVED</th>
                  <th>
                    <button className={styles.sortButton} type="button" onClick={() => toggleSort("inHandDate")}>
                      IN-HAND{sortMarker("inHandDate")}
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => {
                  const config = statusConfig[(order.status as StatusKey) || "pending"] || statusConfig.pending;
                  const StatusIcon = config.icon;
                  const eventTime = formatEventTime(order);
                  return (
                    <tr key={order.id}>
                      <td className={`${styles.orderColumn} ${styles.orderNumber}`}>{order.order_number || `#${order.order_id}`}</td>
                      <td className={styles.customerColumn}>
                        <button
                          type="button"
                          className={styles.customerName}
                          onClick={() => order.member_id && navigate(`/buy-user-admin-management/${order.member_id}`)}
                        >
                          {order.customer_name || "Member"}
                        </button>
                        <span className={styles.subText}>{order.customer_email || "-"}</span>
                        {order.card_last4 && <span className={styles.subText}>**** {order.card_last4}</span>}
                      </td>
                      <td className={styles.statusColumn}>
                        <span className={`${styles.statusButton} ${config.pill}`}>
                          <StatusIcon size={13} />
                          {config.label}
                        </span>
                      </td>
                      <td className={styles.dateColumn}>
                        {formatDate(order.purchased_at, false)}
                        <span className={styles.subText}>{formatTime(order.purchased_at)}</span>
                      </td>
                      <td className={styles.deliveryColumn}><span className={styles.typeChip}>{defaultDeliveryTypeLabel(order.fulfillment_type, deliveryTypeOptions)}</span></td>
                      <td className={styles.eventCell}>
                        <span className={styles.eventName} title={order.performer || order.event_name || ""}>
                          {order.performer || order.event_name || "-"}
                        </span>
                      </td>
                      <td>
                        {formatEventDate(order)}
                        {eventTime && <span className={styles.subText}>{eventTime}</span>}
                      </td>
                      <td className={styles.venueCell}><span className={styles.truncate} title={order.venue || ""}>{order.venue || "-"}</span></td>
                      <td className={styles.center}>
                        Sec {order.section || "-"}
                        <span className={styles.subText}>Row {order.row || "-"}</span>
                      </td>
                      <td className={styles.center}><strong>{order.quantity}</strong></td>
                      <td className={styles.right}>{formatCurrency(order.price_per_ticket)}</td>
                      <td className={styles.right}>
                        <strong>{formatCurrency(order.total_spent)}</strong>
                        <span className={styles.subText}>{formatCurrency(order.total_ticket_cost)} + fees</span>
                      </td>
                      <td className={`${styles.right} ${styles.savings}`}>{formatCurrency(order.total_saved)}</td>
                      <td>{formatInHandDate(order.in_hand_date)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className={styles.footer}>
            <span>Page {page} of {totalPages} ({total} orders)</span>
            <div className={styles.pagination}>
              <button type="button" disabled={page <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>
                <ChevronLeft size={14} />
                Prev
              </button>
              <button type="button" disabled={page >= totalPages} onClick={() => setPage((value) => Math.min(totalPages, value + 1))}>
                Next
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BuyAdminFulfillment;
